import { useEffect, useRef, useState } from "react";
import "../../styles/BaseChat.scss"; 
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getComponentData, getViewData } from "../../core/views";
import { CHAT_VIEW_NAME, EDITOR_VIEW_NAME, ERROR_HELP_STATE } from "../../core/constants";
import { translate } from "../../core/i18n";
import { getDomainCfg, store } from "../../core/state";
import { BatteryQuotaIndicator } from "./BatteryQuotaIndicator";
import { ChatMessage, ErrorMessage, JumbotronMessage, TypingMessage, WarningMessage } from "./ChatMessages";
import { IntelligentChatMessage } from "./IntelligentChatMessage";
import { CreditIndicator } from './CreditIndicator';

// In BaseChat Props
interface BaseChatProps {
  messages: any[];
  sendMessage: (message: string) => void;
  serverIsResponding: boolean;
  hasProjectContext: boolean;
  resetChat?: boolean; // Optional
  llmSwitchAvailable?: boolean;
  modelOptions?: { id: string; name: string; roles?: string[] }[];
  selectedModelId?: string;
  onChangeModel?: (id: string) => void;
  isModelAvailable?: (model: { id: string; name: string; roles?: string[] }) => boolean;
}

const BaseChat = ({ messages, sendMessage, serverIsResponding, hasProjectContext, resetChat, llmSwitchAvailable, modelOptions, selectedModelId, onChangeModel, isModelAvailable } : BaseChatProps) => {
  const [chatState] = store.useState(CHAT_VIEW_NAME);
  const [editorData] = store.useState(EDITOR_VIEW_NAME);
  const [newMessage, setNewMessage] = useState("");
  const [showJumbotron, setShowJumbotron] = useState(false);  
  const [sendCode, setSendCode] = useState(false);
  const [isUserAtBottom, setIsUserAtBottom] = useState(true);
  const [fatalError, setFatalError] = useState(false);
  const [lineCount, setLineCount] = useState(2); // Mindestens 2 Zeilen
  const [prevMessagesLength, setPrevMessagesLength] = useState(messages.length);
  const [clickedUpsell, setClickedUpsell] = useState(false);
  const maxLines = 8;
  
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const messageContainer = messagesEndRef.current;
    messageContainer.addEventListener("scroll", handleScroll);
  
    return () => {
      messageContainer.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (resetChat) {
      setFatalError(false);
    }
  }, [resetChat]);

  useEffect(() => {
    setTimeout(() => {
      if (isUserAtBottom && serverIsResponding) {
        const messageContainer = messagesEndRef.current;
        if (messageContainer) {
          messageContainer.scrollTop = messageContainer.scrollHeight;
        }
      } else if (isUserAtBottom && messages && messages.length > prevMessagesLength) { //fix this bug, only scroll after getting a new message
        const lastMessageId = `message-${messages.length - 1}`;
        const lastMessageElement = document.getElementById(lastMessageId);
        if (lastMessageElement) {
          lastMessageElement.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
      setPrevMessagesLength(messages.length);
    }, 300);
  }, [messages, isUserAtBottom]);

  const handleScroll = () => {
    const isAtBottom = messagesEndRef.current.scrollHeight - messagesEndRef.current.clientHeight <= messagesEndRef.current.scrollTop + 1;
    setIsUserAtBottom(isAtBottom);
  };
  
  const onSendMessage = async () => {
    const finalMessage = processBeforeSending();
    if (finalMessage.trim()) {
      await sendMessage(finalMessage);
      setNewMessage("");
    }
    setShowJumbotron(false);
    setIsUserAtBottom(true);
  };

  const onTryReload = () => {
    window.location.reload();
  }

  const onShowCreditsStore = () => {    
    if (!clickedUpsell) {
      window.open(getDomainCfg().chatSettings.creditPurchaseUrl, '_blank');
      setClickedUpsell(true);
    } else {
      window.location.reload();
    }
  }

  const newMessageChanged = (e) => {
    setNewMessage(e.target.value);
    // Berechne Zeilenanzahl basierend auf Inhalt, mindestens 2
    const lines = e.target.value.split('\n').length;
    setLineCount(Math.max(2, Math.min(lines, maxLines)));
  };

  const setQuestionInInput = (question) => {
    setShowJumbotron(false)
    if (question !== undefined) {
      setNewMessage(question);
    }
  }

  const handleKeyPress = (e) => {
    if (fatalError) return;  
    
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (chatState.data?.credits.credits <= 0) {
        onShowCreditsStore();
        return;
      }
      // Prüfe ob ein nicht verfügbares Modell ausgewählt ist
      const selectedModel = selectedModelId ? modelOptions?.find(opt => opt.id === selectedModelId) : null;
      const isSelectedModelAvailable = selectedModel && isModelAvailable ? isModelAvailable(selectedModel) : true;
      if (selectedModel && isModelAvailable && !isSelectedModelAvailable) {
        // Öffne Upgrade-Seite in neuem Tab
        const upgradeUrl = chatState?.cfg?.upgradeUrl || "https://app.vibeventure.de/produkte/";
        window.open(upgradeUrl, '_blank');
        return;
      }
      onSendMessage();
    }
    //if enter and shift is pressed, add carriage return and resize the input one line
    if (e.key === "Enter" && e.shiftKey && lineCount < maxLines) {
      setLineCount(Math.max(2, lineCount + 1));
    }
  };

  const handleLinkClick = () => {
    setShowJumbotron(!showJumbotron);
  };

  const processBeforeSending = () => {
    if (hasProjectContext) {
      const editorCode = editorData.data.editorCode;
      let contextMessage = "";

      if (editorData.data.errors && editorData.data.errors.length > 0) {
        const errorState = getComponentData(ERROR_HELP_STATE);
        if (errorState.currentError) {
          contextMessage += `[UserContextData title="Fehlermeldung und mein Code"]\n${translate('chat-error-message')}${errorState.currentError.message}\n`;
          contextMessage += `${translate('chat-error-element')}${errorState.currentError.item}\n`;
          contextMessage += `${translate('chat-error-help')}${errorState.currentError.shortHelp}\n`;
          contextMessage += `\n\`\`\`code\n${editorCode}\n\`\`\`\n[/UserContextData]\n\n`;
        }
      } else if (editorCode && editorCode.trim().length > 0) {
        contextMessage = `[UserContextData title="Mein Code"]\n\`\`\`code\n${editorCode}\n\`\`\`\n[/UserContextData]\n\n`;
      }

      return contextMessage + newMessage;
    }
    return newMessage;
  }
  
  const renderMessage = (message, index) => {
    const messageId = `message-${index}`;
    switch (message.messageType) {
      case "error":
        return <ErrorMessage key={index} id={messageId} message={message.msg} />;
      case "warning":
        return <WarningMessage key={index} id={messageId} message={message.msg} />;
      case "typing":
        return <TypingMessage key={index} id={messageId} message={message.msg} user={message.user} color={message.color} />;
      default:
        return <IntelligentChatMessage key={index} id={messageId} message={message.msg} user={message.user} color={message.color} />;
    }
  };

  const messageItems = messages.map(renderMessage);
  
  // Wenn showJumbotron true ist, fügen Sie den Jumbotron in die Nachrichtenliste ein
  if (showJumbotron) {  
    messageItems.push(
      <JumbotronMessage key="jumbotron" questions={editorData.project?.question?.list || []} onClick={setQuestionInInput} />
    );
  }

  const possibleQuestionsLink = <p className="fakeLink text-center" onClick={handleLinkClick}>
    { editorData.project?.question?.link || translate("chat-question-button")}          
  </p>

  const shownValue = chatState.data?.credits.credits;
  const creditsEmpty = shownValue === 0; // && initialLimit >= 0;

  // Prüfe ob das ausgewählte Modell verfügbar ist
  const selectedModel = selectedModelId ? modelOptions?.find(opt => opt.id === selectedModelId) : null;
  const isSelectedModelAvailable = selectedModel && isModelAvailable ? isModelAvailable(selectedModel) : true;
  const modelNotAvailable = selectedModel && isModelAvailable && !isSelectedModelAvailable;
  
  // Füge Warnmeldung für nicht verfügbare Modelle hinzu (ähnlich wie bei Credits)
  if (modelNotAvailable && !creditsEmpty) {
    const upgradeUrl = chatState?.cfg?.upgradeUrl || "https://app.vibeventure.de/produkte/";
    const upgradeMessage = 
      `Dieses Modell ist nicht in Ihrem aktuellen Plan enthalten. <a href="${upgradeUrl}" target="_blank">Bitte upgraden Sie Ihren Plan</a>`;
    const upgradeWarningMessage = {
      user: getDomainCfg().chatSettings?.botName || "System",
      color: "red",
      messageType: "warning",
      msg: upgradeMessage,
      timestamp: new Date()
    };
    // Prüfe ob die Meldung bereits vorhanden ist
    if (!messages.some(msg => msg.messageType === "warning" && msg.msg === upgradeMessage)) {
      messageItems.push(
        <WarningMessage key="upgrade-warning" id="upgrade-warning-message" message={upgradeMessage} />
      );
    }
  }

  let sendButtonIcon = serverIsResponding ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> : <FontAwesomeIcon icon="paper-plane" />
  let sendButtonClasses = "btn btn-primary input-group-append";
  let sendButtonTitle = "";
  
  // Setze Tooltip für nicht verfügbare Modelle (wenn keine anderen Fehler vorliegen)
  if (modelNotAvailable && !fatalError && !creditsEmpty) {
    sendButtonTitle = translate("chat-model-not-available-tooltip") || "Dieses Modell ist nicht in Ihrem aktuellen Plan enthalten. Bitte upgraden Sie Ihren Plan.";
  }
  
  let sendButton = <button className={sendButtonClasses} onClick={onSendMessage} disabled={serverIsResponding || fatalError || modelNotAvailable } title={sendButtonTitle} data-testid="send-message-button">{ sendButtonIcon }</button>

  if (fatalError) {
    sendButtonIcon = <FontAwesomeIcon icon="triangle-exclamation" />
    sendButton = <button className={sendButtonClasses} onClick={onTryReload} disabled={ serverIsResponding } data-testid="send-message-button">{ sendButtonIcon }</button>
  } else if (creditsEmpty) {
    sendButtonIcon = <FontAwesomeIcon icon="store" />
    sendButtonTitle = translate("chat-no-credits-tooltip") || "Keine Credits verfügbar. Klicken Sie hier, um Credits zu kaufen.";
    sendButton = <button className={sendButtonClasses} onClick={onShowCreditsStore} disabled={serverIsResponding } title={sendButtonTitle} data-testid="send-message-button">{ sendButtonIcon }</button>
  }  
  return (
    <div className="chatContainer">  
      <div className="chatMessages" ref={messagesEndRef}>{messageItems}</div>    
      { messages.length <= 1 && editorData.project?.question?.list?.length > 0 && possibleQuestionsLink }
      <div className="chatInput">
        <div className="chatInputWrapper">
          <textarea
            className="form-control chatInputTextarea"
            value={newMessage}
            onChange={newMessageChanged}
            onKeyPress={handleKeyPress}
            placeholder="Geben Sie hier eine Nachricht ein..."
            disabled={fatalError}
            style={{ minHeight: `${30 * Math.max(lineCount, 2)}px` }}
          />
          <div className="chatInputControls">
            { llmSwitchAvailable && modelOptions && modelOptions.length > 0 && (
              <select
                className="form-select chatModelSelect"
                value={selectedModelId || modelOptions[0].id}
                onChange={(e) => onChangeModel && onChangeModel(e.target.value)}
                disabled={serverIsResponding || fatalError}
              >
                {modelOptions.map(opt => {
                  const isAvailable = isModelAvailable ? isModelAvailable(opt) : true;
                  const displayName = isAvailable ? opt.name : `${opt.name} (Upgrade)`;
                  return (
                    <option 
                      key={opt.id} 
                      value={opt.id}
                      style={!isAvailable ? { color: '#999', fontStyle: 'italic' } : {}}
                    >
                      {displayName}
                    </option>
                  );
                })}
              </select>
            ) }
            { sendButton }
          </div>
        </div>
      </div>
      <p className="text-center disclaimer">
         { getDomainCfg().chatSettings.disclaimer }          
      </p>
    </div>
  );
};

export default BaseChat;
